'use client'

import { i18n } from '@quadralize/i18n'
import { cn } from '@quadralize/utils'
import { CloudUpload, File as FileIcon, TrashBinMinimalistic } from '@solar-icons/react'
import type * as React from 'react'
import { createContext, forwardRef, useCallback, useContext, useEffect, useState } from 'react'
import {
  type Accept,
  type DropzoneOptions,
  type DropzoneState,
  type FileError,
  type FileRejection,
  useDropzone,
} from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { toast } from '../../../utils/toast'
import { Button } from '../../ui/button/button'

const BYTES_IN_KB = 1024
const MAX_PERCENT = 100
const MIN_PERCENT = 0

// --- Context ---

interface FileUploadContextProps extends DropzoneState {
  files: File[]
  setFiles: React.Dispatch<React.SetStateAction<File[]>>
  removeFile: (file: File) => void
  rejections: readonly FileRejection[]
  maxFiles?: number
  maxSize?: number
  accept?: Accept
}

const FileUploadContext = createContext<FileUploadContextProps | null>(null)

const useFileUpload = () => {
  return useContext(FileUploadContext)
}

// --- Components ---

interface FileUploadProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  value?: File[]
  onValueChange?: (files: File[]) => void
  dropzoneOptions?: Omit<DropzoneOptions, 'disabled'>
  maxFiles?: number
  disabled?: boolean
}

const formatFileSize = (bytes?: number) => {
  if (!bytes) return ''
  const units = ['B', 'KB', 'MB', 'GB']
  let size = bytes
  let unitIndex = 0
  while (size >= BYTES_IN_KB && unitIndex < units.length - 1) {
    size /= BYTES_IN_KB
    unitIndex++
  }
  return `${size.toFixed(unitIndex === 0 ? 0 : 1)}${units[unitIndex]}`
}

const formatAcceptedTypes = (accept?: Accept) => {
  if (!accept) return ''
  return Object.values(accept)
    .flat()
    .map((ext) => ext.replace('.', '').toUpperCase())
    .join(', ')
}

const translateError = (
  error: FileError,
  constraints: { maxSize?: number; accept?: Accept },
  t: (key: string, options?: Record<string, unknown>) => string,
) => {
  if (error.code === 'file-invalid-type') {
    const types = formatAcceptedTypes(constraints.accept)
    return `Este formato de arquivo não é aceito.${types ? ` Use apenas: ${types}` : ''}`
  }
  if (error.code === 'file-too-large') {
    const size = formatFileSize(constraints.maxSize)
    return `O arquivo é muito grande.${size ? ` O limite máximo permitido é ${size}.` : ''}`
  }
  if (error.code === 'too-many-files') {
    return t('fileUpload.maxFilesError')
  }
  if (error.code === 'file-too-small') {
    return t('fileUpload.tooSmallError')
  }
  return error.message || t('fileUpload.unknownError')
}

const FileUpload = forwardRef<HTMLDivElement, FileUploadProps>(
  (
    { children, className, value, onValueChange, dropzoneOptions, maxFiles, disabled, ...props },
    ref,
  ) => {
    const { t } = useTranslation('ui', { i18n })
    const [files, setFiles] = useState<File[]>(value || [])

    // Sync external value
    useEffect(() => {
      if (value !== undefined) {
        setFiles(value)
      }
    }, [value])

    const handleFilesChange = useCallback(
      (newFiles: File[]) => {
        setFiles(newFiles)
        onValueChange?.(newFiles)
      },
      [onValueChange],
    )

    const onDrop = useCallback(
      (acceptedFiles: File[], fileRejections: FileRejection[]) => {
        // If maxFiles is 1, replace. If not, append.
        if (maxFiles === 1) {
          handleFilesChange(acceptedFiles)
        } else {
          // Logic to append but respect maxFiles limit if necessary
          const availableSlots = maxFiles ? maxFiles - files.length : Infinity
          const filesToAdd = acceptedFiles.slice(0, availableSlots)
          handleFilesChange([...files, ...filesToAdd])
        }

        if (fileRejections.length > 0) {
          fileRejections.forEach(({ file, errors }) => {
            const errorMsg = translateError(
              errors[0] as FileError,
              {
                maxSize: dropzoneOptions?.maxSize,
                accept: dropzoneOptions?.accept,
              },
              t,
            )
            const title = `Não foi possível enviar ${file.name}`

            toast.error(title, {
              description: errorMsg,
            })
          })
        }
      },
      [files, maxFiles, handleFilesChange, dropzoneOptions?.maxSize, dropzoneOptions?.accept, t],
    )

    const dropzoneState = useDropzone({
      ...dropzoneOptions,
      onDrop,
      maxFiles,
      disabled,
    })

    const removeFile = useCallback(
      (fileToRemove: File) => {
        const newFiles = files.filter((f) => f !== fileToRemove)
        handleFilesChange(newFiles)
      },
      [files, handleFilesChange],
    )

    return (
      <FileUploadContext.Provider
        value={{
          ...dropzoneState,
          files,
          setFiles,
          removeFile,
          rejections: dropzoneState.fileRejections,
          maxFiles,
          maxSize: dropzoneOptions?.maxSize,
          accept: dropzoneOptions?.accept,
        }}
      >
        <div ref={ref} className={cn('w-full flex flex-col gap-4', className)} {...props}>
          {children}
        </div>
      </FileUploadContext.Provider>
    )
  },
)
FileUpload.displayName = 'FileUpload'

// --- Trigger ---

const FileUploadTrigger = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => {
    const { t } = useTranslation('ui', { i18n })
    const context = useFileUpload()

    if (!context) {
      return (
        <div
          ref={ref}
          className={cn(
            'border-2 border-dashed rounded-lg p-8 opacity-50 cursor-not-allowed flex flex-col items-center justify-center gap-2 text-center',
            className,
          )}
          {...props}
        >
          {children || <p className="text-sm font-medium">FileUploadProvider ausente</p>}
        </div>
      )
    }

    const { getRootProps, getInputProps, isDragActive, isDragReject, maxSize, accept } = context

    const sizeText = formatFileSize(maxSize)
    const typesText = formatAcceptedTypes(accept)

    return (
      <div
        {...getRootProps()}
        ref={ref}
        className={cn(
          'border-2 border-dashed rounded-lg p-8 transition-colors cursor-pointer flex flex-col items-center justify-center gap-2 text-center outline-none ring-offset-bg-base focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2',
          isDragActive
            ? 'border-primary bg-primary/5'
            : 'border-border-default hover:bg-bg-subtle/50',
          isDragReject && 'border-error bg-error/5',
          className,
        )}
        {...props}
      >
        <input {...getInputProps()} />
        {children ? (
          children
        ) : (
          <>
            <div className="p-3 bg-bg-subtle rounded-full mb-2">
              <CloudUpload className="w-6 h-6 text-primary" />
            </div>
            <div className="flex flex-col gap-1">
              <p className="font-medium text-text-primary">Clique ou arraste arquivos aqui</p>
              <p className="text-xs text-text-tertiary">
                {sizeText
                  ? t('fileUpload.maxSizeLabel', { size: sizeText })
                  : t('fileUpload.noSizeLimit')}
                {typesText ? ` (${typesText})` : ''}
              </p>
            </div>
          </>
        )}
      </div>
    )
  },
)
FileUploadTrigger.displayName = 'FileUploadTrigger'

// --- Content (List) ---

const FileUploadContent = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => {
    const context = useFileUpload()

    if (!context) {
      return (
        <div ref={ref} className={cn('grid gap-3', className)} {...props}>
          {children}
        </div>
      )
    }

    const { files } = context

    if (files.length === 0 && !children) return null

    return (
      <div ref={ref} className={cn('grid gap-3', className)} {...props}>
        {children}
      </div>
    )
  },
)
FileUploadContent.displayName = 'FileUploadContent'

// --- Item ---

interface FileUploadItemProps extends React.HTMLAttributes<HTMLDivElement> {
  file: File
  progress?: number
}

const FileUploadItem = forwardRef<HTMLDivElement, FileUploadItemProps>(
  ({ className, file, progress, ...props }, ref) => {
    const context = useFileUpload()
    const [preview, setPreview] = useState<string | null>(null)

    useEffect(() => {
      if (file.type.startsWith('image/')) {
        const objectUrl = URL.createObjectURL(file)
        setPreview(objectUrl)
        return () => URL.revokeObjectURL(objectUrl)
      }
    }, [file])

    const formatSize = (bytes: number) => {
      if (bytes === 0) return '0 B'
      const k = BYTES_IN_KB
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return titleCase(`${parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`)
    }

    // Simple Title Case helper for unit (e.g., Kb -> KB logic handled above mostly)
    const titleCase = (s: string) => s

    return (
      <div
        ref={ref}
        className={cn(
          'relative flex items-center gap-4 p-3 rounded-md border border-border-default bg-bg-base/50 overflow-hidden',
          className,
        )}
        {...props}
      >
        {/* Preview / Icon */}
        <div className="shrink-0 h-10 w-10 rounded-md bg-bg-subtle overflow-hidden flex items-center justify-center border border-border-default">
          {preview ? (
            <img src={preview} alt={file.name} className="h-full w-full object-cover" />
          ) : (
            <FileIcon className="h-5 w-5 text-text-tertiary" />
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0 flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-text-primary truncate max-w-[200px]">
              {file.name}
            </span>
            <span className="text-xs text-text-tertiary shrink-0 ml-2">
              {formatSize(file.size)}
            </span>
          </div>

          {/* Progress Bar (Optional) */}
          {typeof progress === 'number' && (
            <div className="h-1.5 w-full bg-bg-subtle rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-300 ease-out"
                style={{ width: `${Math.min(MAX_PERCENT, Math.max(MIN_PERCENT, progress))}%` }}
              />
            </div>
          )}
        </div>

        {/* Remove Button */}
        {context?.removeFile && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="shrink-0 text-text-tertiary hover:text-error hover:bg-error/10 h-8 w-8"
            onClick={(e) => {
              e.stopPropagation()
              context.removeFile(file)
            }}
          >
            <TrashBinMinimalistic className="h-4 w-4" />
          </Button>
        )}
      </div>
    )
  },
)
FileUploadItem.displayName = 'FileUploadItem'

// --- Exports ---
export { FileUpload, FileUploadContent, FileUploadItem, FileUploadTrigger, useFileUpload }
